import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchAllPages } from '../DashboardPage/DashboardPage.duck';
import { PAID_ORDER_TRANSITIONS, AWAITING_PAYMENT_TRANSITIONS } from './orderStatus';

export const TAB_SELLING = 'selling';
export const TAB_BUYING = 'buying';

// ================ Action types ================ //

export const FETCH_ORDERS_REQUEST = 'app/OrdersPage/FETCH_ORDERS_REQUEST';
export const FETCH_ORDERS_SUCCESS = 'app/OrdersPage/FETCH_ORDERS_SUCCESS';
export const FETCH_ORDERS_ERROR = 'app/OrdersPage/FETCH_ORDERS_ERROR';
export const SEND_REMINDER_REQUEST = 'app/OrdersPage/SEND_REMINDER_REQUEST';
export const SEND_REMINDER_SUCCESS = 'app/OrdersPage/SEND_REMINDER_SUCCESS';
export const SEND_REMINDER_ERROR = 'app/OrdersPage/SEND_REMINDER_ERROR';

// ================ Reducer ================ //

const initialState = {
  tab: null,
  transactionRefs: [],
  fetchInProgress: false,
  fetchOrdersError: null,
  // { [txId]: 'sending' | 'sent' | 'error' }
  reminders: {},
};

export default function ordersPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOrdersError: null,
        // Don't flash the other tab's orders while this one loads.
        transactionRefs: state.tab === payload.tab ? state.transactionRefs : [],
        tab: payload.tab,
      };
    case FETCH_ORDERS_SUCCESS:
      return { ...state, fetchInProgress: false, transactionRefs: payload };
    case FETCH_ORDERS_ERROR:
      return { ...state, fetchInProgress: false, fetchOrdersError: payload };
    case SEND_REMINDER_REQUEST:
      return { ...state, reminders: { ...state.reminders, [payload]: 'sending' } };
    case SEND_REMINDER_SUCCESS:
      return { ...state, reminders: { ...state.reminders, [payload]: 'sent' } };
    case SEND_REMINDER_ERROR:
      return { ...state, reminders: { ...state.reminders, [payload]: 'error' } };
    default:
      return state;
  }
}

// ================ Thunks ================ //

const refsOf = responses =>
  responses.flatMap(r => r.data.data).map(({ id, type }) => ({ id, type }));

// Each order is fetched once: a tx is either paid or awaiting payment.
const uniqueRefs = refs => {
  const seen = new Set();
  return refs.filter(ref => {
    const id = ref.id.uuid;
    return seen.has(id) ? false : seen.add(id);
  });
};

export const fetchOrders = tab => (dispatch, getState, sdk) => {
  const only = tab === TAB_BUYING ? 'order' : 'sale';
  dispatch({ type: FETCH_ORDERS_REQUEST, payload: { tab } });

  const baseParams = {
    only,
    include: ['listing', 'listing.images', 'customer', 'provider'],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'metadata',
    ],
    'fields.listing': ['title'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    // Uncropped, so whole slabs and landscape cards both show.
    'fields.image': ['variants.scaled-small', 'variants.square-small'],
    'limit.images': 1,
  };
  const query = params => sdk.transactions.query(params);

  return Promise.all([
    fetchAllPages(query, { ...baseParams, lastTransitions: PAID_ORDER_TRANSITIONS }),
    fetchAllPages(query, {
      ...baseParams,
      lastTransitions: AWAITING_PAYMENT_TRANSITIONS,
      meta_isActive: true,
    }),
  ])
    .then(([paid, awaiting]) => {
      const responses = [...paid, ...awaiting];
      responses.forEach(r => dispatch(addMarketplaceEntities(r)));
      dispatch({ type: FETCH_ORDERS_SUCCESS, payload: uniqueRefs(refsOf(responses)) });
    })
    .catch(e => dispatch({ type: FETCH_ORDERS_ERROR, payload: storableError(e), error: true }));
};

// A message in the order's conversation. Sharetribe emails the buyer about
// new messages, so this doubles as an email reminder.
export const sendPaymentReminder = (txId, content) => (dispatch, getState, sdk) => {
  dispatch({ type: SEND_REMINDER_REQUEST, payload: txId.uuid });
  return sdk.messages
    .send({ transactionId: txId, content })
    .then(() => dispatch({ type: SEND_REMINDER_SUCCESS, payload: txId.uuid }))
    .catch(() => dispatch({ type: SEND_REMINDER_ERROR, payload: txId.uuid }));
};

export const loadData = params => dispatch =>
  dispatch(fetchOrders(params?.tab === TAB_BUYING ? TAB_BUYING : TAB_SELLING));
